import { createRouter, createWebHistory, type Router } from 'vue-router'

import './meta'
import { cancelInFlightGuard, authGuard, permissionGuard } from './guards'
import { appRoutes } from './routes'

import { runNavResets } from '@/app/shell/navResetRegistry'
import { useRouteAnalytics } from '@/app/shell/useRouteAnalytics'
import { sealGetStartedSettingsSurface } from '@/features/global-surface'
import { sealSettingsChildRoutes } from '@/features/workspace-settings'
import { useEnv } from '@/shared/config'

// One place composes the router and registers the guard chain in CONTRACT ORDER (05 §11):
// cancelInFlight → auth → permission. EXACTLY these three beforeEach guards — the v3
// mega-beforeEach ceases to exist here: its query-callback branches move to the owning
// features (ORP-18611) and its per-nav store resets to `registerNavReset` contributions;
// navigation itself is auth/permission/cancel only (master-plan rule 5).
//
// No singleton `router` export (contract §4 sketches one): the instance needs `useEnv()`,
// which is only valid after bootstrap's `validateEnv()` — a module-eval singleton would
// crash any importer that loads first. `app/bootstrap.ts` calls this factory once instead.
export function createAppRouter(): Router {
  sealGetStartedSettingsSurface()
  // Module 22's settings child-route registry closes HERE, one statement before the router reads
  // `appRoutes` (contract 22 §11/§14). A pane registered after this point could never resolve, so
  // the seam's one real footgun becomes a loud throw instead of a blank page.
  sealSettingsChildRoutes()

  const router = createRouter({
    history: createWebHistory(useEnv().BASE_URL),
    routes: appRoutes,
  })

  router.beforeEach(cancelInFlightGuard) // 1. abort the prev nav's controller, rebind signal
  router.beforeEach(authGuard) // 2. token gate: guestOnly→home, protected+no-token→Login(next)
  router.beforeEach(permissionGuard) // 3. RBAC gate: sysadmin / permission / dashboard-only

  // Route side-effects, never navigation decisions (contract 05 §8/§11).
  router.afterEach(runNavResets) // feature-contributed per-nav state resets (ORP-18611)
  router.afterEach(useRouteAnalytics().hook) // GTM page-view analytics (env-gated; ORP-18612)

  return router
}
